import React, { FunctionComponent, memo } from 'react';
import { Flex, Skeleton } from 'antd';
import {
  TrackType,
  TrackItem as TrackItemType,
} from '@monorepo/remotion-ninja';
import AssetComponent from '../../../../components/asset/asset';
import { AssetType } from '@monorepo/types';
import { imageBlobManager } from '../../../../managers/image-blob-manager';
import {
  getTrackBorderColor,
  getTrackColor,
  getTrackItemLabel,
  getTrackItemTimelinePosition,
} from './track-types';
import WaveformAudio from './wave';
import HoverText from './hover-text';

interface TrackItemProps {
  item: TrackItemType;
  trackType: TrackType;
  totalFrames: number;
  onItemClick?: (item: TrackItemType) => void;
}

const TrackItem: FunctionComponent<TrackItemProps> = ({
  item,
  trackType,
  totalFrames,
  onItemClick,
}) => {
  const { startPercent, widthPercent } = getTrackItemTimelinePosition(
    item,
    totalFrames
  );
  const baseColor = getTrackColor(trackType);
  const borderColor = getTrackBorderColor(trackType);
  const itemLabel = getTrackItemLabel(item);

  // Base style for all track items
  const baseStyle: React.CSSProperties = {
    position: 'absolute',
    left: `${startPercent}%`,
    width: `${widthPercent}%`,
    height: '80%',
    top: '10%',
    backgroundColor: `${baseColor}22`, // Add transparency
    borderRadius: '6px', // More rounded corners
    border: `1px solid ${borderColor}`,
    overflow: 'hidden',
    cursor: 'pointer',
  };

  const handleClick = () => {
    if (onItemClick) {
      onItemClick(item);
    }
  };

  // Render different item types
  if (trackType === TrackType.Video || trackType === TrackType.Image) {
    const itemData = item.data as any;
    const url = itemData.src;
    const isVideo = trackType === TrackType.Video || url?.endsWith('mp4');
    const shouldShowImage = url && !isVideo;

    let style = baseStyle;

    if (shouldShowImage && url) {
      let imageUrl = url;

      // If we have an image blob manager, try to get the image from it
      try {
        if (
          imageBlobManager &&
          typeof imageBlobManager.getImage === 'function'
        ) {
          imageUrl = imageBlobManager.getImage(url);
        }
      } catch (e) {
        // Fallback to original URL if there's an error
      }

      style = {
        ...style,
        backgroundImage: `url(${imageUrl})`,
        backgroundSize: 'contain',
        backgroundPosition: 'center',
        backgroundRepeat: 'repeat',
      };
    }

    return (
      <div
        className={`track-item ${trackType.toLowerCase()}-item`}
        style={style}
        onClick={handleClick}
        title={itemLabel}
      >
        {!url ? (
          <Flex
            justify={'center'}
            style={{ marginTop: '5px' }}
            align={'bottom'}
          >
            No Asset
          </Flex>
        ) : null}
        {isVideo && url ? (
          <AssetComponent
            shouldLazy={false}
            width={'100%'}
            height={'100%'}
            asset={{ url, type: AssetType.Video }}
          />
        ) : null}
      </div>
    );
  }

  if (trackType === TrackType.Text || trackType === TrackType.Thumbnail) {
    return (
      <div
        className={`track-item ${trackType.toLowerCase()}-item`}
        style={{
          ...baseStyle,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '12px',
          padding: '0 8px',
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
        }}
        onClick={handleClick}
        title={itemLabel}
      >
        {itemLabel}
      </div>
    );
  }

  if (trackType === TrackType.Audio) {
    // Audio visualization with repeating pattern
    return (
      <div
        className={`track-item ${trackType.toLowerCase()}-item`}
        style={{
          ...baseStyle,
        }}
        onClick={handleClick}
        title={itemLabel}
      >
        <span className="item-label">
          <HoverText placeholder={'Audio'}>{itemLabel}</HoverText>
        </span>
        <div className="audio-waveform">
          <WaveformAudio
            key={item.data.src}
            waveColor={'#00ab84'}
            url={item.data.src}
          />
        </div>
      </div>
    );
  }
  if (trackType === TrackType.Voiceover) {
    // Audio visualization with repeating pattern
    return (
      <div
        className={`track-item ${trackType.toLowerCase()}-item`}
        style={{
          ...baseStyle,
        }}
        onClick={handleClick}
        title={itemLabel}
      >
        <span className="item-label">
          <HoverText placeholder={'Voiceover'}>{itemLabel}</HoverText>
        </span>
        <div className="audio-waveform">
          <WaveformAudio url={item.data.src} />
        </div>
      </div>
    );
  }

  if (trackType === TrackType.Gif) {
    // GIF with repeating pattern
    return (
      <div
        className="track-item gif-item"
        style={{
          ...baseStyle,
          backgroundImage: `
            repeating-linear-gradient(
              45deg,
              ${baseColor}33,
              ${baseColor}33 10px,
              ${baseColor}22 10px,
              ${baseColor}22 20px
            )
          `,
        }}
        onClick={handleClick}
        title={itemLabel}
      >
        <span className="item-label">GIF</span>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            fontSize: '12px',
            textShadow: '0 0 2px white',
          }}
        >
          {itemLabel}
        </div>
      </div>
    );
  }

  // Default fallback with pattern
  return (
    <div
      className="track-item unknown-item"
      style={{
        ...baseStyle,
        backgroundImage: `
          repeating-linear-gradient(
            -45deg,
            ${baseColor}33,
            ${baseColor}33 5px,
            ${baseColor}22 5px,
            ${baseColor}22 10px
          )
        `,
      }}
      onClick={handleClick}
      title={itemLabel}
    >
      <span className="item-label">Track Item</span>
    </div>
  );
};

// Use memo to prevent unnecessary rerenders
export default memo(TrackItem);
